/**
 * Constantes partagées entre client et serveur pour les chambres
 * Source unique de vérité - ne pas dupliquer ces valeurs ailleurs
 */

// Limites réglementaires françaises pour les chambres d'hôtes
export const MAX_ROOMS_PER_ESTABLISHMENT = 5

// Limite photos par chambre
export const MAX_PHOTOS_PER_ROOM = 5

// Types de lit disponibles (alignés sur l'enum Prisma BedType)
export const BED_TYPES = [
  'SINGLE',
  'DOUBLE',
  'QUEEN',
  'KING',
  'BUNK_2',
  'SOFA_BED',
  'FUTON_BED',
  'TRUNDLE',
  'BABY_CRIB',
  'CHILD_BED',
  'EXTRA_BED',
] as const
export type BedType = (typeof BED_TYPES)[number]

// Équipements de chambre disponibles — catalogue enrichi catégorisé
// Les clés existantes (tv, wifi, climatisation, coffre, bureau, vue_jardin, vue_montagne, vue_mer, balcon, terrasse)
// sont conservées pour la rétrocompatibilité avec les données en base.
// Les équipements cuisine partagés/communs (coin_cuisine, refrigerateur, micro_ondes, cuisine_partagee)
// ont été déplacés côté Établissement car ils décrivent un service commun, pas un équipement de chambre.
// Note: `plateau_courtoisie` appartenait à la catégorie "chambre" jusqu'en 04/2026, déplacé vers
// "boissons chaudes" (clé technique `cuisine` conservée) pour cohérence fonctionnelle.
export const ROOM_EQUIPMENTS = [
  // Chambre
  'linge_de_lit',
  'climatisation',
  'coin_salon',
  'tv',
  'vod',
  'armoire',
  'bureau',
  'coffre',
  'spa_prive',
  'lit_bebe_equip',
  'chaise_haute',
  'baignoire_bebe',
  'table_langer',
  // Salle de bains
  'douche',
  'douche_italienne',
  'baignoire',
  'baignoire_balneo',
  'lavabo',
  'vasque',
  'seche_cheveux',
  'seche_serviettes',
  'linge_toilette',
  'peignoirs',
  'savon_shampoing',
  // WC
  'wc_prive',
  'wc_partage',
  'toilette_seche',
  // Boissons chaudes (catégorie technique `cuisine` — label UI renommé en 04/2026)
  'plateau_courtoisie',
  'mini_refrigerateur',
  'machine_cafe',
  'bouilloire',
  'the_tisane',
  // Extérieur
  'terrasse_partagee',
  'terrasse',
  'terrasse_privee',
  'balcon',
  'mobilier_jardin',
  'transats',
  'barbecue',
  // Vues
  'vue_jardin',
  'vue_piscine',
  'vue_campagne',
  'vue_montagne',
  'vue_mer',
  'vue_lac',
  'vue_riviere',
  'vue_foret',
  'vue_rue',
  'vue_ville',
  'vue_cour',
  'vue_parc',
] as const
export type RoomEquipment = (typeof ROOM_EQUIPMENTS)[number]

// Catégories d'équipements (source de vérité pour le groupement UI)
export const EQUIPMENT_CATEGORY_KEYS = [
  'chambre',
  'salle_de_bains',
  'wc',
  'cuisine',
  'exterieur',
  'vues',
] as const
export type EquipmentCategory = (typeof EQUIPMENT_CATEGORY_KEYS)[number]

export const EQUIPMENT_BY_CATEGORY: Record<EquipmentCategory, readonly RoomEquipment[]> = {
  chambre: ['linge_de_lit', 'climatisation', 'coin_salon', 'tv', 'vod', 'armoire', 'bureau', 'coffre', 'spa_prive', 'lit_bebe_equip', 'chaise_haute', 'baignoire_bebe', 'table_langer'],
  salle_de_bains: ['douche', 'douche_italienne', 'baignoire', 'baignoire_balneo', 'lavabo', 'vasque', 'seche_cheveux', 'seche_serviettes', 'linge_toilette', 'peignoirs', 'savon_shampoing'],
  wc: ['wc_prive', 'wc_partage', 'toilette_seche'],
  cuisine: ['plateau_courtoisie', 'mini_refrigerateur', 'machine_cafe', 'bouilloire', 'the_tisane'],
  exterieur: ['terrasse_partagee', 'terrasse', 'terrasse_privee', 'balcon', 'mobilier_jardin', 'transats', 'barbecue'],
  vues: ['vue_jardin', 'vue_piscine', 'vue_campagne', 'vue_montagne', 'vue_mer', 'vue_lac', 'vue_riviere', 'vue_foret', 'vue_rue', 'vue_ville', 'vue_cour', 'vue_parc'],
}
